import React from 'react';
import {RequirementUtilsService} from '../RequirementUtils.service';
import {
	useCreateRequirementMutation,
	useDeleteRequirementMutation,
	useGetRequirementByIdLazyQuery,
} from '../hooks/requirements.generated';
import {useCloneRequirement} from '../hooks/useCloneRequirement';
import {useRequirementsVersions} from '../hooks/useRequirementsVersions';
import {useGetConvolutesQuery} from 'features/AdminSection/hooks/convolutes.generated';
import {ConvoluteType, Requirement, RequirementStatus, UserRole} from 'types';
import {useGetGdprRuleQuery} from 'features/AdminSection/hooks/gdprRules.generated';
import {useUserContext} from 'authentication/UserContext';
import {
	UpdateRequirement,
	UpdateRequirementAttachments,
	useRequirementAttachmentsUpdater,
	useRequirementsFormElementsRenderer,
	useRequirementUpdater,
} from '../hooks/requirementFormHooks';
import {createHrefFromRequirement} from '../requirementLinks.utils';
import {useNavigate} from 'react-router-dom';
import {PanelType} from '@fluentui/react';
import {useRequirementsTranslation} from '../hooks/useRequirementsTranslation';
import {FaqCommand} from 'components/Faq/FaqCommand';
import {NewRequirementVersionPanel} from './NewRequirementVersionPanel';
import {FaqPanelMode} from 'components/Faq/FaqPanel';
import {InfinityPage} from 'components/InfinityPage/InfinityPage';
import {useSelection} from 'hooks';

const {createCommonCreationInputFields} = new RequirementUtilsService();

export const RequirementsPageRequirementForm: React.FC = () => {
	const {t} = useRequirementsTranslation();

	const [getRequirmentOriginal] = useGetRequirementByIdLazyQuery();
	const [createRequirementMutation] = useCreateRequirementMutation();
	const [deleteRequirementMutation] = useDeleteRequirementMutation();
	const {cloneRequirement} = useCloneRequirement();
	const {hasEditableChildren} = useRequirementsVersions();

	const navigate = useNavigate();
	const onViewRequirement = React.useCallback(
		(req: Requirement) => {
			const href: string = createHrefFromRequirement(req);
			return navigate(href);
		},
		[navigate],
	);

	const hasCorrectRightsToEdit = (selectedItem: Requirement) => {
		const vexClusterMatch = selectedItem.vexClusters.some(vc =>
			myVexClusters.some(mvc => mvc.id === vc.id),
		);

		return !isAdmin && !vexClusterMatch;
	};

	const refetchQueries = React.useMemo(() => [], []);

	const {isAdmin, isVex, isVexSupport, myVexClusters} = useUserContext();

	const disableDelete = React.useCallback(
		(selectedItem: Requirement) => {
			const requirementStatus = selectedItem.status;

			return (
				hasCorrectRightsToEdit(selectedItem) ||
				requirementStatus === RequirementStatus.Approval ||
				requirementStatus === RequirementStatus.Final ||
				selectedItem.status === RequirementStatus.Interim ||
				selectedItem.status === RequirementStatus.InterimCompleted
			);
		},
		[isAdmin, myVexClusters],
	);

	const disableEdit = React.useCallback(
		(selectedItem: Requirement) => {
			return (
				hasEditableChildren(selectedItem) ||
				hasCorrectRightsToEdit(selectedItem) ||
				selectedItem.status === RequirementStatus.Approval ||
				selectedItem.status === RequirementStatus.Interim ||
				selectedItem.status === RequirementStatus.InterimCompleted
			);
		},
		[isAdmin, myVexClusters],
	);

	const {data: convolutes} = useGetConvolutesQuery();
	const convolute = React.useMemo(
		() =>
			convolutes?.convolutes?.find(
				c => c.convoluteType === ConvoluteType.Requirement,
			),
		[convolutes],
	);

	const {data: gdprRule} = useGetGdprRuleQuery({
		variables: {
			id: convolute?.gdprRule?.id ?? '',
		},
	});

	const rule = React.useMemo(() => gdprRule?.gdprRule, [gdprRule]);

	const createRequirement = React.useCallback(
		async (requirement: Requirement) => {
			if (requirement.status === RequirementStatus.Final) {
				const {data} = await getRequirmentOriginal({
					variables: {id: requirement.id},
				});
				const origComparableRequirement = data?.requirement as Requirement;
				const res = await cloneRequirement(
					requirement as Requirement,
					refetchQueries,
					rule,
					origComparableRequirement,
				);
				return res?.data?.createRequirement?.requirement;
			}

			const res = await createRequirementMutation({
				variables: {
					input: createCommonCreationInputFields(requirement, rule),
				},
				refetchQueries,
			});
			navigate(0);
			return res.data?.createRequirement?.requirement;
		},
		[cloneRequirement, createRequirementMutation, refetchQueries, rule],
	);

	const updateRequirement: UpdateRequirement =
		useRequirementUpdater(refetchQueries);

	const updateRequirementFinal = async (item: Requirement) => {
		await updateRequirement(item);
		navigate(0);
	};

	const deleteRequirement = React.useCallback(
		async (id: string): Promise<void> => {
			await deleteRequirementMutation({
				variables: {
					input: {id},
				},
				refetchQueries,
			});
		},
		[deleteRequirementMutation, refetchQueries],
	);

	const {
		formDataResult: {data: formData, loading: formDataLoading},
		renderFormElements,
	} = useRequirementsFormElementsRenderer<Requirement>();

	const updateAttachments: UpdateRequirementAttachments =
		useRequirementAttachmentsUpdater();

	const [selection] = useSelection<Requirement>();
	const item: Requirement[] = [];
	return (
		<>
			<NewRequirementVersionPanel formData={formData} />
			<FaqCommand
				faqState={{
					mode: FaqPanelMode.EntityList,
					entity: selection?.getSelection()[0] as any,
					showPanel: false,
				}}
			/>
			<InfinityPage
				items={item}
				entityDisplayName={t('Requirements')}
				entityHeaderText={t('RequirementsHeader')}
				createEntity={createRequirement}
				updateEntity={updateRequirementFinal}
				deleteEntity={deleteRequirement}
				updateEntityAttachments={updateAttachments}
				additionalColumns={[]}
				renderAdditionalFormElements={renderFormElements}
				hideCreate={!isAdmin && !isVex}
				hideEdit={!isAdmin && !isVex}
				hideDelete={isVexSupport || (!isAdmin && !isVex)}
				panelType={PanelType.medium}
				onViewItem={onViewRequirement}
				confirmUpdate
				refetchQueries={refetchQueries}
				disableDelete={disableDelete}
				disableEdit={disableEdit}
				withNameColumn={false}
				withNameColumnInPanel={true}
				customTranslations={{
					deleteSubtextTranslationKey: 'RequirementSubtext',
				}}
			/>
		</>
	);
};
